<script setup lang="ts">
    import { computed } from 'vue';
    import { __ } from '@/js/stores/expressions';
    import SettingsPageLayout from '@/js/components/settings/SettingsPageLayout.vue';
    import LoadingDots from '@/js/components/LoadingDots.vue';
    import TextInput, { TextInputWidth } from '@/js/components/TextInput.vue';
    import SelectInput from '@/js/components/SelectInput.vue';
    import SortArrows from '../components/SortArrows.vue';
    import { useInternalUsersTable } from '@/js/composables/useInternalUsersTable';
    import { useQueryGetCsmUsers } from '@/js/services/api/backOffice/backOfficeQueries';

    const { data: csmUsersData, isLoading } = useQueryGetCsmUsers();

    type Row = {
        tenantName: string;
        userId: string;
        email: string;
        name: string;
        roleName: string;
    };

    const rows = computed<Row[]>(() => {
        const entries = csmUsersData.value ?? {};
        return Object.entries(entries).flatMap(([tenantName, entry]) =>
            entry.tenantUsers.users.map((user) => ({
                tenantName,
                userId: user.id,
                email: user.email ?? '',
                name: [user.firstName, user.lastName].filter(Boolean).join(' '),
                roleName: user.role?.name ?? '',
            }))
        );
    });

    const { search, tenantFilter, tenantNames, setSort, activeSort, visibleRows } =
        useInternalUsersTable(rows);

    const columns = computed<{ field: keyof Row; label: string }[]>(() => [
        { field: 'tenantName', label: __('back-office-users-col-tenant') },
        { field: 'name', label: __('users-table-column-name') },
        { field: 'email', label: __('email') },
        { field: 'roleName', label: __('back-office-users-col-role') },
    ]);
</script>
<template>
    <SettingsPageLayout>
        <template #header>
            <h1 class="heading-lg-light">{{ __('back-office-csm-users') }}</h1>
        </template>
        <template #content>
            <div class="flex flex-wrap items-center gap-3 mb-6">
                <TextInput
                    v-model="search"
                    :width="TextInputWidth.Large"
                    :placeholder="__('back-office-users-search-placeholder')"
                    :aria-label="__('search')"
                />
                <SelectInput
                    v-model="tenantFilter"
                    :aria-label="__('back-office-users-col-tenant')"
                >
                    <option value="">{{ __('back-office-users-all-tenants') }}</option>
                    <option v-for="tenant in tenantNames" :key="tenant" :value="tenant">
                        {{ tenant }}
                    </option>
                </SelectInput>
            </div>
            <p class="mb-4 text-caption text-gray-medium">
                {{ __('back-office-csm-users-count', { count: String(visibleRows.length) }) }}
            </p>
            <div class="overflow-x-auto">
                <table class="w-full border-collapse">
                    <thead>
                        <tr class="border-b border-gray-lightest">
                            <th
                                v-for="col in columns"
                                :key="col.field"
                                class="py-3 pl-3 pr-6 text-left text-caption-bold uppercase"
                            >
                                <span class="flex items-center gap-1">
                                    {{ col.label }}
                                    <SortArrows
                                        :asc-disabled="activeSort(col.field) === 'asc'"
                                        :desc-disabled="activeSort(col.field) === 'desc'"
                                        @sort="(dir) => setSort(col.field, dir)"
                                    />
                                </span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="isLoading">
                            <td colspan="4" class="py-12 text-center"><LoadingDots /></td>
                        </tr>
                        <template v-else>
                            <tr v-if="!visibleRows.length">
                                <td colspan="4" class="py-6 text-body text-gray-medium text-center">
                                    {{ __('back-office-csm-users-empty') }}
                                </td>
                            </tr>
                            <tr
                                v-for="row in visibleRows"
                                :key="row.userId"
                                class="border-b border-gray-lightest"
                            >
                                <td class="py-3 pl-3 pr-6 text-body">{{ row.tenantName }}</td>
                                <td class="py-3 pl-3 pr-6 text-body">{{ row.name }}</td>
                                <td class="py-3 pl-3 pr-6 text-body">{{ row.email }}</td>
                                <td class="py-3 pl-3 pr-6 text-body">{{ row.roleName }}</td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>
        </template>
    </SettingsPageLayout>
</template>
